import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type SystemEventLevel = 'info' | 'warn' | 'error';
export type SystemEventSource = 'ui' | 'edge' | 'db' | 'cron';

export interface SystemEvent {
  id: string;
  level: SystemEventLevel;
  source: SystemEventSource;
  event_key: string;
  domain: string | null;
  entity_type: string | null;
  entity_id: string | null;
  producer_id: string | null;
  actor_user_id: string | null;
  message: string;
  context: Record<string, unknown>;
  request_id: string | null;
  user_agent: string | null;
  ip: string | null;
  created_at: string;
  producer?: {
    id: string;
    name: string;
    slug: string | null;
  } | null;
}

export interface SystemEventFilters {
  search?: string;
  level?: SystemEventLevel | 'all';
  source?: SystemEventSource | 'all';
  domain?: string | 'all';
  producerId?: string;
  range?: '24h' | '7d' | '30d';
  requestId?: string;
}

export interface SystemEventStats {
  errorCount: number;
  warnCount: number;
  infoCount: number;
  lastErrorAt: string | null;
}

export interface ProducerActionRequired {
  producerId: string;
  producerName: string;
  producerSlug: string | null;
  errorCount: number;
  lastErrorAt: string;
}

const PAGE_SIZE = 25;

function getRangeDate(range: '24h' | '7d' | '30d'): Date {
  const now = new Date();
  switch (range) {
    case '24h':
      return new Date(now.getTime() - 24 * 60 * 60 * 1000);
    case '7d':
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    case '30d':
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    default:
      return new Date(now.getTime() - 24 * 60 * 60 * 1000);
  }
}

export function useSystemEventStats(range: '24h' | '7d' | '30d' = '24h') {
  return useQuery({
    queryKey: ['system-event-stats', range],
    queryFn: async (): Promise<SystemEventStats> => {
      const rangeDate = getRangeDate(range);

      // Fetch counts for each level
      const [errorResult, warnResult, infoResult, lastErrorResult] = await Promise.all([
        supabase
          .from('system_events')
          .select('id', { count: 'exact', head: true })
          .eq('level', 'error')
          .gte('created_at', rangeDate.toISOString()),
        supabase
          .from('system_events')
          .select('id', { count: 'exact', head: true })
          .eq('level', 'warn')
          .gte('created_at', rangeDate.toISOString()),
        supabase
          .from('system_events')
          .select('id', { count: 'exact', head: true })
          .eq('level', 'info')
          .gte('created_at', rangeDate.toISOString()),
        supabase
          .from('system_events')
          .select('created_at')
          .eq('level', 'error')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      return {
        errorCount: errorResult.count || 0,
        warnCount: warnResult.count || 0,
        infoCount: infoResult.count || 0,
        lastErrorAt: lastErrorResult.data?.created_at || null,
      };
    },
  });
}

export function useRecentSystemEvents(
  limit: number = 20,
  range: '24h' | '7d' | '30d' = '24h',
  onlyErrors: boolean = false
) {
  return useQuery({
    queryKey: ['recent-system-events', limit, range, onlyErrors],
    queryFn: async (): Promise<SystemEvent[]> => {
      const rangeDate = getRangeDate(range);

      let query = supabase
        .from('system_events')
        .select(`
          *,
          producer:producers!system_events_producer_id_fkey(id, name, slug)
        `)
        .gte('created_at', rangeDate.toISOString())
        .order('created_at', { ascending: false })
        .limit(limit);

      if (onlyErrors) {
        query = query.eq('level', 'error');
      }

      const { data, error } = await query;

      if (error) {
        if (error.code === '42501' || error.message?.includes('permission')) {
          throw new Error('Unauthorized');
        }
        throw error;
      }

      return (data || []) as SystemEvent[];
    },
  });
}

export function useSystemEvents(filters: SystemEventFilters, page: number = 0) {
  return useQuery({
    queryKey: ['system-events', filters, page],
    queryFn: async () => {
      const rangeDate = getRangeDate(filters.range || '24h');
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase
        .from('system_events')
        .select(`
          *,
          producer:producers!system_events_producer_id_fkey(id, name, slug)
        `, { count: 'exact' })
        .gte('created_at', rangeDate.toISOString())
        .order('created_at', { ascending: false })
        .range(from, to);

      // Apply filters
      if (filters.level && filters.level !== 'all') {
        query = query.eq('level', filters.level);
      }

      if (filters.source && filters.source !== 'all') {
        query = query.eq('source', filters.source);
      }

      if (filters.domain && filters.domain !== 'all') {
        query = query.eq('domain', filters.domain);
      }

      if (filters.producerId) {
        query = query.eq('producer_id', filters.producerId);
      }

      if (filters.requestId) {
        query = query.eq('request_id', filters.requestId);
      }

      if (filters.search) {
        query = query.or(`message.ilike.%${filters.search}%,event_key.ilike.%${filters.search}%`);
      }

      const { data, error, count } = await query;

      if (error) {
        if (error.code === '42501' || error.message?.includes('permission')) {
          throw new Error('Unauthorized');
        }
        throw error;
      }

      return {
        events: (data || []) as SystemEvent[],
        totalCount: count || 0,
        pageSize: PAGE_SIZE,
        currentPage: page,
        totalPages: Math.ceil((count || 0) / PAGE_SIZE),
      };
    },
  });
}

export function useProducersActionRequired(range: '24h' | '7d' | '30d' = '24h') {
  return useQuery({
    queryKey: ['producers-action-required', range],
    queryFn: async (): Promise<ProducerActionRequired[]> => {
      const rangeDate = getRangeDate(range);

      // Get all errors grouped by producer_id with join
      const { data, error } = await supabase
        .from('system_events')
        .select(`
          producer_id,
          created_at,
          producer:producers!system_events_producer_id_fkey(id, name, slug)
        `)
        .eq('level', 'error')
        .gte('created_at', rangeDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        if (error.code === '42501' || error.message?.includes('permission')) {
          throw new Error('Unauthorized');
        }
        throw error;
      }

      // Group by producer_id
      const grouped = new Map<string | null, { 
        producer: { id: string; name: string; slug: string | null } | null;
        count: number;
        lastErrorAt: string;
      }>();

      for (const event of data || []) {
        const key = event.producer_id;
        const existing = grouped.get(key);
        if (existing) {
          existing.count++;
        } else {
          grouped.set(key, {
            producer: event.producer as { id: string; name: string; slug: string | null } | null,
            count: 1,
            lastErrorAt: event.created_at,
          });
        }
      }

      // Convert to array and sort by count
      const result: ProducerActionRequired[] = [];
      for (const [producerId, data] of grouped) {
        result.push({
          producerId: producerId || 'system',
          producerName: data.producer?.name || 'System',
          producerSlug: data.producer?.slug || null,
          errorCount: data.count,
          lastErrorAt: data.lastErrorAt,
        });
      }

      return result.sort((a, b) => b.errorCount - a.errorCount);
    },
  });
}

export function usePendingProducersCount() {
  return useQuery({
    queryKey: ['pending-verification-count'],
    queryFn: async () => {
      // Only count producers who have actively submitted for verification
      // (verification_submitted_at IS NOT NULL) and are not yet verified
      const { count, error } = await supabase
        .from('producers')
        .select('id', { count: 'exact', head: true })
        .not('verification_submitted_at', 'is', null)
        .eq('verified', false);

      if (error) {
        console.error('Error fetching pending verification count:', error);
        return 0;
      }

      return count || 0;
    },
  });
}
